import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  useTheme,
  Card,
  CardContent,
  Avatar,
  Chip,
  Stack,
  alpha,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  TextField,
} from "@mui/material";
import {
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  People as PeopleIcon,
  ShoppingCart as ShoppingCartIcon,
  AttachMoney as AttachMoneyIcon,
  AccountBalance as AccountBalanceIcon,
} from "@mui/icons-material";
import {
  CustomersCard,
  ExpensesCard,
  RevenueCard,
  RevenueTrend,
  SalesCard,
  SalesTrend,
  SoldProductsChart,
  allCompanyBranchesView,
  allRegions,
  allUsers,
} from "../../data/Axios/queries";
import { graphqlQuery, fetchDataEngine } from "../../data/Axios/DynamicService";
import { companyManagementUrl, userManagementUrl } from "../../config";
import { tokens } from "../../theme";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
  AreaChart,
  Area,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { handleGlobalChange } from "./regionbranchuserchange";

const base_url = companyManagementUrl.uri;
const user_Url = userManagementUrl.uri;
// Stat Card Component
const StatCard = ({ title, value, change, icon, color }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";
  const isPositive = change >= 0;

  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        background: isDark
          ? `linear-gradient(135deg, ${colors.primary[400]} 0%, ${colors.primary[500]} 100%)`  // #1F2A40 → #141b2d
          : `linear-gradient(135deg, ${alpha(color, 0.1)} 0%, ${alpha(color, 0.05)} 100%)`,
        border: isDark
          ? `1px solid ${alpha(color, 0.35)}`
          : `1px solid ${alpha(color, 0.2)}`,
        borderRadius: 3,
        transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: isDark
            ? `0 12px 28px rgba(0,0,0,0.5), 0 0 0 1px ${alpha(color, 0.4)}`
            : `0 12px 24px ${alpha(color, 0.2)}`,
          borderColor: alpha(color, 0.5),
        },
      }}
    >
      <CardContent sx={{ p: 3 }}>
        <Stack spacing={2}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="body2"
              sx={{
                color: isDark ? colors.grey[300] : "text.secondary",
                fontWeight: 600,
                textTransform: "uppercase",
                letterSpacing: 0.5,
              }}
            >
              {title}
            </Typography>
            <Avatar
              sx={{
                bgcolor: isDark ? alpha(color, 0.2) : alpha(color, 0.15),
                color: color,
                width: 48,
                height: 48,
              }}
            >
              {icon}
            </Avatar>
          </Box>

          <Typography
            variant="h3"
            sx={{
              fontWeight: 700,
              color: isDark ? colors.grey[100] : "text.primary",
              letterSpacing: -0.5,
            }}
          >
            {value}
          </Typography>

          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
            <Chip
              icon={isPositive ? <TrendingUpIcon /> : <TrendingDownIcon />}
              label={`${Math.abs(change)}%`}
              size="small"
              sx={{
                bgcolor: alpha(isPositive ? "#4caf50" : "#f44336", isDark ? 0.2 : 0.1),
                color: isPositive ? "#4caf50" : "#f44336",
                fontWeight: 600,
                "& .MuiChip-icon": {
                  color: isPositive ? "#4caf50" : "#f44336",
                },
              }}
            />
            <Typography variant="caption" color={isDark ? colors.grey[400] : "text.secondary"}>
              vs last month
            </Typography>
          </Box>
        </Stack>
      </CardContent>
    </Card>
  );
};

// Chart Card Wrapper
const ChartCard = ({ title, subtitle, children, height = 350 }) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);
  const isDark = theme.palette.mode === "dark";

  return (
    <Paper
      elevation={0}
      sx={{
        p: 3,
        height: "100%",
        borderRadius: 3,
        border: `1px solid ${isDark ? alpha(colors.blueAccent[700], 0.5) : alpha(theme.palette.divider, 0.15)}`,
        background: isDark
          ? colors.primary[400]   // #1F2A40 — deep navy (SheriaProFrontend)
          : theme.palette.background.paper,
        transition: "all 0.3s ease",
        "&:hover": {
          boxShadow: isDark
            ? `0 8px 24px rgba(0,0,0,0.4)`
            : `0 8px 24px ${alpha(theme.palette.common.black, 0.12)}`,
        },
      }}
    >
      <Stack spacing={2}>
        <Box>
          <Typography variant="h6" fontWeight={700} gutterBottom color={isDark ? colors.grey[100] : "text.primary"}>
            {title}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color={isDark ? colors.grey[400] : "text.secondary"}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ height }}>{children}</Box>
      </Stack>
    </Paper>
  );
};

const Dashboard = () => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode);

  // Dashboard data states
  const [sales, setSales] = useState({});
  const [customers, setCustomers] = useState({});
  const [revenue, setRevenue] = useState({});
  const [expenses, setExpenses] = useState({});
  const [salesTrend, setSalesTrend] = useState([]);
  const [revenueTrend, setRevenueTrend] = useState([]);
  const [soldProductChart, setSoldProductChart] = useState([]);

  // Date Constraints
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");

  // Filter states
  const [filters, setFilters] = useState({
    region: "All",
    branch: "All",
    user: "All",
  });
  const [regions, setRegions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [users, setUsers] = useState([]);

  const decodedToken = JSON.parse(localStorage.getItem("decodedToken"));
  const companyId = decodedToken?.CompanyId
    ? parseInt(decodedToken.CompanyId)
    : null;

  const COLORS = [
    "#6366f1",
    "#8b5cf6",
    "#ec4899",
    "#f43f5e",
    "#f97316",
    "#eab308",
    "#22c55e",
    "#14b8a6",
  ];

  // Fetch filter options per company
  // Fetch filter options per company
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        if (!companyId) return;

        // Fetch Regions
        const regionInputFields = { companyId: companyId };
        const regionReturnFields = ["id", "regionName"];
        const regionData = await fetchDataEngine(
          "allRegionByColumnValues",
          "region",
          regionInputFields,
          regionReturnFields,
          base_url,
        );
        const regionsList = regionData ? regionData : [];
        setRegions(regionsList);

        // Fetch Branches
        const branchInputFields = { companyId: companyId };
        const branchReturnFields = ["id", "branchName"];
        const branchData = await fetchDataEngine(
          "allBranchesByColumnValues",
          "branchModel",
          branchInputFields,
          branchReturnFields,
          base_url,
        );
        const branchesList = branchData ? branchData : [];
        setBranches(branchesList);

        // Fetch Users
        // Using allUsersView as used in UserManagement scene
        const usersQuery = `
          {
            allUsersView {
              id
              username
              firstName
              lastName
              role
            }
          }
        `;
        const usersRes = await graphqlQuery(usersQuery, user_Url);
        const usersData = usersRes ? usersRes : [];
        setUsers(usersData); // Fallback to firstName if username is missing
      } catch (error) {
        console.error("Failed to load dashboard filters:", error);
      }
    };

    fetchFilters();
  }, []);

  // Fetch dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        const variables = {
          regionId: filters.region === "All" ? null : filters.region,
          branchId: filters.branch === "All" ? null : filters.branch,
          userId: filters.user === "All" ? null : filters.user,
          dateFrom: dateFrom || null,
          dateTo: dateTo || null,
        };

        const formheaders = [
          "salesCard",
          "customersCard",
          "salesTrend",
          "revenueTrend",
          "soldProductsChart",
          "revenueCard",
          "expensesCard",
        ];
        //  "CustomersCard", "lastMonth"];

        for (const header of formheaders) {
          const returnFields =
            header === "salesTrend" || header === "revenueTrend"
              ? ["value", "monthName"]
              : header === "soldProductsChart"
                ? ["value", "name"]
                : ["percentageChange", "currentMonth", "lastMonth"];

          const Dashboardata = await fetchDataEngine(
            header,
            "getMonthly",
            variables,
            returnFields,
            base_url,
          );
          if (header === "salesCard") {
            setSales(Dashboardata);
          } else if (header === "customersCard") {
            setCustomers(Dashboardata);
          } else if (header === "revenueCard") {
            setRevenue(Dashboardata);
          } else if (header === "expensesCard") {
            setExpenses(Dashboardata);
          }
          if (header === "revenueCard") {
            setRevenue(Dashboardata);
          } else if (header === "expensesCard") {
            setExpenses(Dashboardata);
          } else if (header === "salesTrend") {
            setSalesTrend(Dashboardata);
          } else if (header === "revenueTrend") {
            setRevenueTrend(Dashboardata);
          } else if (header === "soldProductsChart") {
            setSoldProductChart(Dashboardata);
          }
        }
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      }
    };

    fetchDashboardData();
  }, [filters]);

  const handleRegionChange = async (e) => {
    const inputfields =
      e.target.value === "All" || e.target.value === ""
        ? { companyId: companyId }
        : { regionId: e.target.value };
    const branchReturnFields = ["id", "branchName"];

    const data = await handleGlobalChange(
      "allBranchesByColumnValues",
      "branchModel",
      inputfields,
      branchReturnFields,
      base_url,
    );
    setBranches(data);
  };
  const handleBranchChange = async (e) => {
    const inputfields =
      e.target.value === "All" || e.target.value === ""
        ? { companyId: companyId }
        : { branchId: e.target.value };
    const branchReturnFields = [
      "id",
      "username",
      "firstName",
      "lastName",
      "role",
    ];
    const data = await handleGlobalChange(
      "allUsersByColumnValues",
      "userView",
      inputfields,
      branchReturnFields,
      userManagementUrl.uri,
    );
    setUsers(data);
  };

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <Paper
          elevation={3}
          sx={{
            p: 2,
            bgcolor: alpha(colors.primary[400], 0.95),
            border: `1px solid ${alpha(theme.palette.divider, 0.1)}`,
          }}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {label}
          </Typography>
          {payload.map((entry, index) => (
            <Typography
              key={index}
              variant="caption"
              sx={{ color: entry.color }}
            >
              {entry.name}: {entry.value.toLocaleString()}
            </Typography>
          ))}
        </Paper>
      );
    }
    return null;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      {/* Page Header */}
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          Dashboard Overview
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Welcome back! Here's what's happening with your business today.
        </Typography>
      </Box>

      {/* Filters */}
      <Box
        sx={{
          mb: 6,
          display: "flex",
          gap: 2,
          flexWrap: "wrap",
          alignItems: "center",
        }}
      >
        {/* Region */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Region</InputLabel>
          <Select
            size="small"
            value={filters.region ?? ""}
            label="Region"
            onChange={(e) => {
              handleRegionChange(e);
              setFilters({ ...filters, region: e.target.value || null });
            }}
          >
            <MenuItem value="">All Regions</MenuItem>
            {regions.map((region) => (
              <MenuItem key={region.id} value={region.id}>
                {region.regionName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Branch */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Branch</InputLabel>
          <Select
            size="small"
            value={filters.branch ?? ""}
            label="Branch"
            onChange={(e) => {
              handleBranchChange(e);
              setFilters({ ...filters, branch: e.target.value || null });
            }}
          >
            <MenuItem value="">All Branches</MenuItem>
            {branches.map((branch) => (
              <MenuItem key={branch.id} value={branch.id}>
                {branch.branchName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* User */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>User</InputLabel>
          <Select
            size="small"
            value={filters.user ?? ""}
            label="User"
            onChange={(e) =>
              setFilters({ ...filters, user: e.target.value || null })
            }
          >
            <MenuItem value="">All Users</MenuItem>
            {users.map((user) => (
              <MenuItem key={user.id} value={user.id}>
                {user.firstName} {user.lastName}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Date From */}
        <TextField
          size="small"
          type="date"
          label="From Date"
          InputLabelProps={{ shrink: true }}
          value={dateFrom}
          sx={{ minWidth: 180 }}
          onChange={(e) => {
            const newDateFrom = e.target.value;
            setDateFrom(newDateFrom);
            if (dateTo && dateTo < newDateFrom) {
              setDateTo(newDateFrom);
            }
            setFilters({ ...filters, dateFrom: newDateFrom });
          }}
        />

        {/* Date To */}
        <TextField
          size="small"
          type="date"
          label="To Date"
          InputLabelProps={{ shrink: true }}
          value={dateTo}
          inputProps={{ min: dateFrom || undefined }}
          sx={{ minWidth: 180 }}
          onChange={(e) => {
            const newDateTo = e.target.value;
            if (!dateFrom || newDateTo >= dateFrom) {
              setDateTo(newDateTo);
            }
            setFilters({ ...filters, dateTo: newDateTo });
          }}
        />

        <Button
          size="small"
          variant="outlined"
          color="secondary"
          onClick={() => {
            setFilters({ region: null, branch: null, user: null });
            setDateFrom("");
            setDateTo("");
          }}
        >
          Reset Filters
        </Button>
      </Box>

      <Grid container spacing={3}>
        {/* Stat Cards */}
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Sales This Month"
            value={`KES ${sales.currentMonth?.toLocaleString() || 0}`}
            change={sales.percentageChange || 0}
            icon={<ShoppingCartIcon />}
            color="#6366f1"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="New Customers"
            value={customers.currentMonth?.toLocaleString() || 0}
            change={customers.percentageChange || 0}
            icon={<PeopleIcon />}
            color="#8b5cf6"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Total Revenue"
            value={`KES ${revenue.currentMonth?.toLocaleString() || 0}`}
            change={revenue.percentageChange || 0}
            icon={<AttachMoneyIcon />}
            color="#22c55e"
          />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <StatCard
            title="Expenses"
            value={`KES ${expenses.currentMonth?.toLocaleString() || 0}`}
            change={expenses.percentageChange || 0}
            icon={<AccountBalanceIcon />}
            color="#f43f5e"
          />
        </Grid>

        {/* Sales Trend Chart */}
        <Grid item xs={12} lg={8}>
          <ChartCard
            title="Sales Trend"
            subtitle="Monthly sales performance over the last 12 months"
            height={400}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={salesTrend}>
                <defs>
                  <linearGradient id="colorSales" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#6366f1" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#6366f1" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={alpha(theme.palette.divider, 0.1)}
                />
                <XAxis
                  dataKey="monthName"
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={{ stroke: alpha(theme.palette.divider, 0.1) }}
                />
                <YAxis
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={{ stroke: alpha(theme.palette.divider, 0.1) }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#6366f1"
                  strokeWidth={3}
                  fillOpacity={1}
                  fill="url(#colorSales)"
                />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>

        {/* Top Products Pie Chart */}
        <Grid item xs={12} lg={4}>
          <ChartCard
            title="Top Products"
            subtitle="Best selling products this month"
            height={400}
          >
            {(() => {
              const sorted = [...soldProductChart].sort((a, b) => b.value - a.value);
              const total = sorted.reduce((sum, item) => sum + item.value, 0);

              return (
                <Box sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
                  {/* Donut chart — no inline labels, hover for details */}
                  <Box sx={{ height: 220, flexShrink: 0 }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={sorted}
                          cx="50%"
                          cy="50%"
                          labelLine={false}
                          outerRadius={85}
                          innerRadius={45}
                          fill="#8884d8"
                          dataKey="value"
                          paddingAngle={1}
                        >
                          {sorted.map((entry, index) => (
                            <Cell
                              key={`cell-${index}`}
                              fill={COLORS[index % COLORS.length]}
                            />
                          ))}
                        </Pie>
                        <Tooltip content={<CustomTooltip />} />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>

                  {/* Scrollable product list */}
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      overflowY: "auto",
                      mt: 1,
                      pr: 0.5,
                      "&::-webkit-scrollbar": { width: 4 },
                      "&::-webkit-scrollbar-track": {
                        bgcolor: "transparent",
                      },
                      "&::-webkit-scrollbar-thumb": {
                        bgcolor: alpha(theme.palette.text.secondary, 0.2),
                        borderRadius: 2,
                      },
                    }}
                  >
                    {sorted.map((entry, index) => {
                      const pct = total > 0 ? ((entry.value / total) * 100).toFixed(1) : 0;
                      return (
                        <Box
                          key={index}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1,
                            py: 0.5,
                            px: 1,
                            borderRadius: 1,
                            transition: "background 0.2s",
                            "&:hover": {
                              bgcolor: alpha(COLORS[index % COLORS.length], 0.08),
                            },
                          }}
                        >
                          {/* Color dot */}
                          <Box
                            sx={{
                              width: 10,
                              height: 10,
                              borderRadius: "50%",
                              flexShrink: 0,
                              bgcolor: COLORS[index % COLORS.length],
                            }}
                          />
                          {/* Product name — full text, wraps if needed */}
                          <Typography
                            variant="caption"
                            sx={{
                              flex: 1,
                              fontSize: 11,
                              lineHeight: 1.4,
                              color: theme.palette.text.primary,
                              wordBreak: "break-word",
                            }}
                          >
                            {entry.name}
                          </Typography>
                          {/* Percentage */}
                          <Typography
                            variant="caption"
                            sx={{
                              flexShrink: 0,
                              fontSize: 11,
                              fontWeight: 600,
                              color: theme.palette.text.secondary,
                            }}
                          >
                            {pct}%
                          </Typography>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })()}
          </ChartCard>
        </Grid>

        {/* Revenue Trend */}
        <Grid item xs={12}>
          <ChartCard
            title="Revenue Performance"
            subtitle="Revenue trend and growth analysis"
            height={350}
          >
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={revenueTrend}>
                <defs>
                  <linearGradient id="colorRevenue" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#22c55e" stopOpacity={0.8} />
                    <stop offset="95%" stopColor="#22c55e" stopOpacity={0.3} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke={alpha(theme.palette.divider, 0.1)}
                />
                <XAxis
                  dataKey="monthName"
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={{ stroke: alpha(theme.palette.divider, 0.1) }}
                />
                <YAxis
                  tick={{ fill: theme.palette.text.secondary, fontSize: 12 }}
                  axisLine={{ stroke: alpha(theme.palette.divider, 0.1) }}
                />
                <Tooltip content={<CustomTooltip />} />
                <Bar
                  dataKey="value"
                  fill="url(#colorRevenue)"
                  radius={[8, 8, 0, 0]}
                />
              </BarChart>
            </ResponsiveContainer>
          </ChartCard>
        </Grid>
      </Grid>
    </Box>
  );
};

export default Dashboard;
